import { supabase } from './client'
import type { RealtimeChannel } from '@supabase/supabase-js'
import { analyticsService } from '@lib/analytics/posthog.service'

// ---- Interfaces -------------------------------------------------------------
export interface AppointmentMessageAttachment {
  id: string
  message_id: string
  storage_path: string
  mime_type: string
  file_size_bytes: number
  width: number | null
  height: number | null
  created_at: string
  signedUrl: string | null
}

export interface AppointmentMessage {
  id: string
  appointment_id: string
  conversation_id?: string | null
  patient_id: string
  provider_id: string
  sender_id: string
  sender_role: 'patient' | 'provider'
  message_text: string | null
  reply_to_message_id?: string | null
  created_at: string
  attachments: AppointmentMessageAttachment[]
}

export interface SendMessageParams {
  appointmentId: string
  patientId: string
  providerId: string
  senderRole: 'patient' | 'provider'
  messageText: string
  replyToMessageId?: string | null
  imageFile?: File | null
}

export interface ChatConsultationStatus {
  success: boolean
  isChatConsultation: boolean
  isActive: boolean
  status?: 'not_started' | 'active' | 'expired' | 'completed' | 'cancelled'
  startsAt?: string
  endsAt?: string
  secondsRemaining?: number
  error?: string
}

export interface ChatConversationRef {
  id: string
  conversation_id?: string | null
}

type ChatActorRole = 'patient' | 'provider'

// ---- Constants --------------------------------------------------------------
const MAX_CHAT_IMAGE_SIZE = 5 * 1024 * 1024
const ALLOWED_MIME_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']
const CHAT_SEEN_STORAGE_PREFIX: Record<ChatActorRole, string> = {
  patient: 'patient-chat-seen:',
  provider: 'provider-chat-seen:',
}

const MESSAGE_SELECT = `
  id,
  appointment_id,
  conversation_id,
  patient_id,
  provider_id,
  sender_id,
  sender_role,
  message_text,
  reply_to_message_id,
  created_at,
  appointment_message_attachments (
    id,
    message_id,
    storage_path,
    mime_type,
    file_size_bytes,
    width,
    height,
    created_at
  )
`

// ---- Helpers ----------------------------------------------------------------
async function createSignedUrl(storagePath: string): Promise<string | null> {
  try {
    const { data, error } = await supabase.storage
      .from('chat-media')
      .createSignedUrl(storagePath, 3600)
    if (error || !data?.signedUrl) return null
    return data.signedUrl
  } catch {
    return null
  }
}

async function mapRow(row: any): Promise<AppointmentMessage> {
  const rawAttachments: any[] = Array.isArray(row.appointment_message_attachments)
    ? row.appointment_message_attachments
    : []

  const attachments: AppointmentMessageAttachment[] = await Promise.all(
    rawAttachments.map(async (a) => ({
      id: a.id,
      message_id: a.message_id,
      storage_path: a.storage_path,
      mime_type: a.mime_type,
      file_size_bytes: a.file_size_bytes,
      width: a.width ?? null,
      height: a.height ?? null,
      created_at: a.created_at,
      signedUrl: await createSignedUrl(a.storage_path),
    }))
  )

  return {
    id: row.id,
    appointment_id: row.appointment_id,
    conversation_id: row.conversation_id ?? null,
    patient_id: row.patient_id,
    provider_id: row.provider_id,
    sender_id: row.sender_id,
    sender_role: row.sender_role,
    message_text: row.message_text,
    reply_to_message_id: row.reply_to_message_id ?? null,
    created_at: row.created_at,
    attachments,
  }
}

function mapRowPlain(row: any): AppointmentMessage {
  return {
    id: row.id,
    appointment_id: row.appointment_id,
    conversation_id: row.conversation_id ?? null,
    patient_id: row.patient_id,
    provider_id: row.provider_id,
    sender_id: row.sender_id,
    sender_role: row.sender_role,
    message_text: row.message_text,
    reply_to_message_id: row.reply_to_message_id ?? null,
    created_at: row.created_at,
    attachments: [],
  }
}

function getConversationKey(appointmentId: string, conversationId?: string | null) {
  return conversationId || appointmentId
}

function getSeenStorageKey(actorRole: ChatActorRole, actorId: string) {
  return `${CHAT_SEEN_STORAGE_PREFIX[actorRole]}${actorId}`
}

function createChannelName(prefix: string, scope: string) {
  return `${prefix}:${scope}:${Math.random().toString(36).slice(2, 10)}`
}

function readSeenMap(actorRole: ChatActorRole, actorId: string): Record<string, string> {
  if (typeof window === 'undefined') return {}

  try {
    const raw = window.localStorage.getItem(getSeenStorageKey(actorRole, actorId))
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? parsed as Record<string, string> : {}
  } catch {
    return {}
  }
}

function writeSeenMap(actorRole: ChatActorRole, actorId: string, seenMap: Record<string, string>) {
  if (typeof window === 'undefined') return

  try {
    window.localStorage.setItem(getSeenStorageKey(actorRole, actorId), JSON.stringify(seenMap))
  } catch {
    // ignore storage failures
  }
}

async function getLatestIncomingMessageMap(
  actorRole: ChatActorRole,
  actorId: string,
  senderRole: ChatActorRole,
): Promise<Record<string, string>> {
  const actorColumn = actorRole === 'patient' ? 'patient_id' : 'provider_id'

  try {
    const { data, error } = await supabase
      .from('appointment_messages')
      .select('appointment_id, conversation_id, created_at')
      .eq(actorColumn, actorId)
      .eq('sender_role', senderRole)
      .order('created_at', { ascending: false })

    if (error) throw error

    const latestByConversation: Record<string, string> = {}
    for (const row of data || []) {
      const key = getConversationKey((row as any).appointment_id, (row as any).conversation_id)
      if (!latestByConversation[key]) {
        latestByConversation[key] = (row as any).created_at
      }
    }

    return latestByConversation
  } catch {
    return {}
  }
}

// ---- Service ----------------------------------------------------------------
export const chatService = {
  getConversationKey,

  getSeenMessageMap(actorId: string, actorRole: ChatActorRole = 'patient') {
    return readSeenMap(actorRole, actorId)
  },

  markConversationSeen(
    actorId: string,
    appointmentId: string,
    conversationId?: string | null,
    seenAt = new Date().toISOString(),
    actorRole: ChatActorRole = 'patient',
  ) {
    const seenMap = readSeenMap(actorRole, actorId)
    seenMap[getConversationKey(appointmentId, conversationId)] = seenAt
    writeSeenMap(actorRole, actorId, seenMap)
  },

  async markAppointmentChatAsSeen(patientId: string, appointmentId: string, seenAt = new Date().toISOString()) {
    const conversationId = await this.getConversationId(appointmentId)
    this.markConversationSeen(patientId, appointmentId, conversationId, seenAt, 'patient')
  },

  async markProviderAppointmentChatAsSeen(providerId: string, appointmentId: string, seenAt = new Date().toISOString()) {
    const conversationId = await this.getConversationId(appointmentId)
    this.markConversationSeen(providerId, appointmentId, conversationId, seenAt, 'provider')
  },

  async getLatestIncomingProviderMessageMap(patientId: string): Promise<Record<string, string>> {
    return getLatestIncomingMessageMap('patient', patientId, 'provider')
  },

  async getLatestIncomingPatientMessageMap(providerId: string): Promise<Record<string, string>> {
    return getLatestIncomingMessageMap('provider', providerId, 'patient')
  },

  getUnreadConversationKeys(
    actorId: string,
    conversations: ChatConversationRef[],
    latestIncomingMap: Record<string, string>,
    actorRole: ChatActorRole = 'patient',
  ) {
    const seenMap = readSeenMap(actorRole, actorId)
    const unreadKeys = new Set<string>()

    for (const conversation of conversations) {
      const key = getConversationKey(conversation.id, conversation.conversation_id)
      const latestIncomingAt = latestIncomingMap[key]
      if (!latestIncomingAt) continue
      const seenAt = seenMap[key]
      if (!seenAt || latestIncomingAt > seenAt) {
        unreadKeys.add(key)
      }
    }

    return unreadKeys
  },

  async getConversationId(appointmentId: string): Promise<string | null> {
    try {
      const { data } = await supabase
        .from('appointments')
        .select('conversation_id')
        .eq('id', appointmentId)
        .single()
      return data?.conversation_id ?? null
    } catch {
      return null
    }
  },

  async getConsultationStatus(appointmentId: string): Promise<ChatConsultationStatus> {
    try {
      const { data, error } = await supabase.rpc('get_chat_consultation_status', {
        appointment_id_param: appointmentId,
      })
      if (error) throw error
      if (!data || typeof data !== 'object') {
        return { success: true, isChatConsultation: false, isActive: true }
      }
      const s = data as any
      return {
        success: s.success ?? true,
        isChatConsultation: s.isChatConsultation ?? false,
        isActive: s.isActive ?? true,
        status: s.status,
        startsAt: s.startsAt,
        endsAt: s.endsAt,
        secondsRemaining: s.secondsRemaining,
        error: s.error,
      }
    } catch {
      return { success: true, isChatConsultation: false, isActive: true }
    }
  },

  async getMessages(appointmentId: string): Promise<AppointmentMessage[]> {
    try {
      const conversationId = await this.getConversationId(appointmentId)

      const baseQuery = supabase
        .from('appointment_messages')
        .select(MESSAGE_SELECT)

      const scoped = conversationId
        ? baseQuery.eq('conversation_id', conversationId)
        : baseQuery.eq('appointment_id', appointmentId)

      const { data, error } = await scoped.order('created_at', { ascending: true })

      if (!error && data) {
        return Promise.all(data.map(mapRow))
      }

      // fallback without attachment join
      const { data: fallback } = await supabase
        .from('appointment_messages')
        .select('id, appointment_id, patient_id, provider_id, sender_id, sender_role, message_text, reply_to_message_id, created_at')
        .eq('appointment_id', appointmentId)
        .order('created_at', { ascending: true })

      return (fallback ?? []).map(mapRowPlain)
    } catch {
      return []
    }
  },

  async getMessagesPage(
    appointmentId: string,
    limit = 20,
    beforeCreatedAt?: string,
  ): Promise<{ messages: AppointmentMessage[]; hasMore: boolean; oldestCreatedAt: string | null }> {
    try {
      const conversationId = await this.getConversationId(appointmentId)

      let q = supabase
        .from('appointment_messages')
        .select(MESSAGE_SELECT)

      q = conversationId
        ? (q as any).eq('conversation_id', conversationId)
        : (q as any).eq('appointment_id', appointmentId)

      if (beforeCreatedAt) {
        q = (q as any).lt('created_at', beforeCreatedAt)
      }

      // fetch one extra to detect whether there are more
      const { data, error } = await (q as any)
        .order('created_at', { ascending: false })
        .limit(limit + 1)

      if (error || !data) {
        return { messages: [], hasMore: false, oldestCreatedAt: null }
      }

      const hasMore = data.length > limit
      const slice = hasMore ? data.slice(0, limit) : data
      const mapped = await Promise.all(slice.map(mapRow))
      // reverse so oldest is first (ascending display order)
      mapped.reverse()

      return {
        messages: mapped,
        hasMore,
        oldestCreatedAt: mapped.length > 0 ? mapped[0].created_at : null,
      }
    } catch {
      return { messages: [], hasMore: false, oldestCreatedAt: null }
    }
  },

  async sendMessage(params: SendMessageParams): Promise<{ success: boolean; error?: string }> {
    try {
      const { data: authData } = await supabase.auth.getUser()
      if (!authData?.user) {
        analyticsService.capture('chat_message_send_failed', {
          source: 'chat_service',
          error_type: 'auth',
          sender_role: params.senderRole,
          platform: 'web',
        })
        return { success: false, error: 'Not authenticated' }
      }

      const text = params.messageText.trim()
      const hasImage = Boolean(params.imageFile)

      if (!text && !hasImage) {
        analyticsService.capture('chat_message_send_failed', {
          source: 'chat_service',
          error_type: 'validation',
          sender_role: params.senderRole,
          platform: 'web',
        })
        return { success: false, error: 'Message is empty' }
      }

      const status = await this.getConsultationStatus(params.appointmentId)
      if (status.isChatConsultation && !status.isActive) {
        analyticsService.capture('chat_message_send_failed', {
          source: 'chat_service',
          error_type: 'consultation_inactive',
          sender_role: params.senderRole,
          platform: 'web',
        })
        return {
          success: false,
          error:
            status.status === 'expired'
              ? 'This chat consultation has ended.'
              : 'This chat consultation is not active yet.',
        }
      }

      const conversationId = await this.getConversationId(params.appointmentId)

      const payload: Record<string, any> = {
        appointment_id: params.appointmentId,
        patient_id: params.patientId,
        provider_id: params.providerId,
        sender_id: authData.user.id,
        sender_role: params.senderRole,
        message_text: text || null,
        reply_to_message_id: params.replyToMessageId ?? null,
      }
      if (conversationId) payload.conversation_id = conversationId

      const { data: inserted, error: insertErr } = await supabase
        .from('appointment_messages')
        .insert(payload)
        .select('id')
        .single()

      if (insertErr) throw insertErr

      // Upload image if provided
      if (params.imageFile && inserted?.id) {
        const result = await this.uploadImage(params.appointmentId, authData.user.id, params.imageFile)
        if (result.success) {
          const attachPayload: Record<string, any> = {
            message_id: inserted.id,
            storage_path: result.storagePath,
            mime_type: result.mimeType,
            file_size_bytes: result.fileSize,
            width: null,
            height: null,
          }
          if (conversationId) attachPayload.conversation_id = conversationId
          await supabase.from('appointment_message_attachments').insert(attachPayload)
        }
      }

      analyticsService.capture('chat_message_send_success', {
        source: 'chat_service',
        sender_role: params.senderRole,
        has_image: hasImage,
        platform: 'web',
      })

      return { success: true }
    } catch (err: any) {
      analyticsService.capture('chat_message_send_failed', {
        source: 'chat_service',
        error_type: 'exception',
        sender_role: params.senderRole,
        platform: 'web',
      })
      return { success: false, error: err?.message ?? 'Failed to send message' }
    }
  },

  async uploadImage(
    appointmentId: string,
    userId: string,
    file: File
  ): Promise<{ success: true; storagePath: string; mimeType: string; fileSize: number } | { success: false; error: string }> {
    if (!ALLOWED_MIME_TYPES.includes(file.type)) {
      return { success: false, error: 'Only JPG, PNG, or WEBP images are allowed.' }
    }
    if (file.size > MAX_CHAT_IMAGE_SIZE) {
      return { success: false, error: 'Image must be 5 MB or smaller.' }
    }

    const ext = file.type === 'image/png' ? 'png' : file.type === 'image/webp' ? 'webp' : 'jpg'
    const storagePath = `${appointmentId}/${userId}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`

    const { error } = await supabase.storage.from('chat-media').upload(storagePath, file, {
      contentType: file.type,
      upsert: false,
    })

    if (error) return { success: false, error: error.message }
    return { success: true, storagePath, mimeType: file.type, fileSize: file.size }
  },

  subscribeToMessages(
    appointmentId: string,
    conversationId: string | null,
    onNewMessage: (msg: AppointmentMessage) => void
  ): RealtimeChannel {
    const filter = conversationId
      ? `conversation_id=eq.${conversationId}`
      : `appointment_id=eq.${appointmentId}`

    const channel = supabase
      .channel(createChannelName('chat', appointmentId))
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'appointment_messages', filter },
        async (payload) => {
          const msg = await mapRow(payload.new)
          onNewMessage(msg)
        }
      )
      .subscribe()

    return channel
  },

  subscribeToPatientIncomingMessages(patientId: string, onNewMessage: (msg: AppointmentMessage) => void): RealtimeChannel {
    return supabase
      .channel(createChannelName('patient-chat-incoming', patientId))
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'appointment_messages',
          filter: `patient_id=eq.${patientId}`,
        },
        async (payload) => {
          const row = payload.new as any
          if (row?.sender_role !== 'provider') return
          const msg = await mapRow(row)
          onNewMessage(msg)
        },
      )
      .subscribe()
  },

  subscribeToProviderIncomingMessages(providerId: string, onNewMessage: (msg: AppointmentMessage) => void): RealtimeChannel {
    return supabase
      .channel(createChannelName('provider-chat-incoming', providerId))
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'appointment_messages',
          filter: `provider_id=eq.${providerId}`,
        },
        async (payload) => {
          const row = payload.new as any
          if (row?.sender_role !== 'patient') return
          const msg = await mapRow(row)
          onNewMessage(msg)
        },
      )
      .subscribe()
  },

  unsubscribe(channel: RealtimeChannel) {
    supabase.removeChannel(channel)
  },
}
